import React, { useState, useEffect } from "react";
import { X, Plus, TruckIcon, Trash2, Package, MoveRight, MapPin } from "lucide-react";

export default function DeliveryGroupingPopup({
  open,
  onClose,
  awardedItems = [],
  vehicles = [],
  onConfirm,
  suppliersData
}) {
  const [groups, setGroups] = useState([]);

  /* ===================== INITIALIZE ===================== */

  useEffect(() => {
    if (!open) return;

    const suppliers = {};

    awardedItems.forEach(item => {
      if (!suppliers[item.supplierId]) {
        suppliers[item.supplierId] = {
          supplierId: item.supplierId,
          supplierName: item.supplierName,
          items: []
        };
      }
      suppliers[item.supplierId].items.push(item);
    });

    setGroups([
      {
        groupId: "DG-1",
        suppliers,
        vehicleId: "",
        deliveryCharge: ""
      }
    ]);
  }, [open, awardedItems]);

  if (!open) return null;

  /* ===================== HELPERS ===================== */

  const addGroup = () => {
    setGroups(prev => [
      ...prev,
      {
        groupId: `DG-${prev.length + 1}`,
        suppliers: {},
        vehicleId: "",
        deliveryCharge: ""
      }
    ]);
  };

  /* ---------- MOVE SINGLE ITEM ---------- */

  const moveItem = (item, fromGroupId, toGroupId) => {
    if (!toGroupId || fromGroupId === toGroupId) return;

    setGroups(prev =>
      prev.map(group => {
        if (group.groupId === fromGroupId) {
          const suppliers = { ...group.suppliers };
          const supplier = suppliers[item.supplierId];

          const remainingItems = supplier.items.filter(
            i => i.item_id !== item.item_id
          );

          if (remainingItems.length === 0) {
            delete suppliers[item.supplierId];
          } else {
            suppliers[item.supplierId] = {
              ...supplier,
              items: remainingItems
            };
          }

          return { ...group, suppliers };
        }

        if (group.groupId === toGroupId) {
          const suppliers = { ...group.suppliers };

          suppliers[item.supplierId] ??= {
            supplierId: item.supplierId,
            supplierName: item.supplierName,
            items: []
          };

          suppliers[item.supplierId] = {
            ...suppliers[item.supplierId],
            items: [...suppliers[item.supplierId].items, item]
          };

          return { ...group, suppliers };
        }

        return group;
      })
    );
  };

  /* ---------- MOVE ENTIRE SUPPLIER ---------- */

  const moveSupplier = (supplier, fromGroupId, toGroupId) => {
    if (!toGroupId || fromGroupId === toGroupId) return;

    setGroups(prev =>
      prev.map(group => {
        if (group.groupId === fromGroupId) {
          const suppliers = { ...group.suppliers };
          delete suppliers[supplier.supplierId];
          return { ...group, suppliers };
        }

        if (group.groupId === toGroupId) {
          const suppliers = { ...group.suppliers };

          suppliers[supplier.supplierId] = suppliers[supplier.supplierId]
            ? {
                ...suppliers[supplier.supplierId],
                items: [
                  ...suppliers[supplier.supplierId].items,
                  ...supplier.items
                ]
              }
            : { ...supplier };

          return { ...group, suppliers };
        }

        return group;
      })
    );
  };

  const groupWeight = suppliers =>
    Object.values(suppliers)
      .flatMap(s => s.items)
      .reduce((sum, i) => sum + Number(i.totalWeight || 0), 0);

  const handleConfirm = () => {
    onConfirm(
      groups.map(g => ({
        groupId: g.groupId,
        vehicle_type: g.vehicleId,
        total_weight: groupWeight(g.suppliers),
        delivery_charge: Number(g.deliveryCharge || 0),
        items: Object.values(g.suppliers).flatMap(s => s.items)
      }))
    );
  };

  /* ===================== UI ===================== */

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center">
      <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b pb-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TruckIcon className="text-blue-600" />
            Delivery Group Configuration
          </h2>
          <button onClick={onClose}><X /></button>
        </div>

        {/* Groups */}
        <div className="space-y-6">
          {groups.map(group => (
            <div key={group.groupId} className="border rounded-xl p-4">
              <div className="flex justify-between mb-4">
                <h3 className="font-semibold">{group.groupId}</h3>
                {groups.length > 1 && (
                  <Trash2
                    size={18}
                    className="text-red-500 cursor-pointer"
                    onClick={() =>
                      setGroups(prev =>
                        prev.filter(g => g.groupId !== group.groupId)
                      )
                    }
                  />
                )}
              </div>

              {/* SUPPLIERS */}
            {/*   {Object.values(group.suppliers).map(supplier => (
                <div key={supplier.supplierId} className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex flex-col">
  <span className="font-semibold text-slate-700">
    {supplier.supplierName}
  </span>

  {supplier.distance_km > 0 ? (
    <span className="mt-1 text-xs text-slate-500 flex items-center gap-1">
      <MapPin size={12} />
      {supplier.distance_km} km
    </span>
  ):(
    <></>
  )}
</div>

                    <select
                      className="border rounded px-2 py-1 text-xs"
                      onChange={e =>
                        moveSupplier(
                          supplier,
                          group.groupId,
                          e.target.value
                        )
                      }
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Move Supplier To
                      </option>
                      {groups
                        .filter(g => g.groupId !== group.groupId)
                        .map(g => (
                          <option key={g.groupId} value={g.groupId}>
                            {g.groupId}
                          </option>
                        ))}
                    </select>
                  </div>

                
                  <div className="space-y-2">
                    {supplier.items.map(item => (
                      <div
                        key={item.item_id}
                        className="flex justify-between items-center bg-slate-100 rounded p-3"
                      >
                        <div>
                          <div className="font-medium flex gap-2 items-center">
                            <Package size={14} />
                            {item.item_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            Qty {item.quantity} • {item.totalWeight} kg
                          </div>
                        </div>

                        <select
                          className="border rounded px-2 py-1"
                          defaultValue=""
                          onChange={e =>
                            moveItem(
                              item,
                              group.groupId,
                              e.target.value
                            )
                          }
                        >
                          <option value="" disabled>
                            Move Item To
                          </option>
                          {groups
                            .filter(g => g.groupId !== group.groupId)
                            .map(g => (
                              <option key={g.groupId} value={g.groupId}>
                                {g.groupId}
                              </option>
                            ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))} */}

              {/* SUPPLIERS */}
{Object.values(group.suppliers).map(supplier => {
  // 🔑 Find supplier master data by supplierId
  const supplierMeta = suppliersData.find(
    s => s.vendor_id?.toString() === supplier.supplierId?.toString()
  );
 
  const distanceKm = supplierMeta? supplierMeta.distanceKm: null

  return (
    <div key={supplier.supplierId} className="mb-4">
      <div className="flex justify-between items-center mb-2">
        {/* SUPPLIER NAME + DISTANCE */}
        <div className="flex flex-row space-x-4">
          <span className="font-semibold text-slate-700">
            {supplier.supplierName}
          </span>

          {distanceKm !== null && distanceKm > 0 ?(
            <span className="mt-1 text-xs text-slate-500 flex items-center gap-1">
              <MapPin size={12} />
              {distanceKm} km
            </span>
          ):(
            <></>
          )}
        </div>

        {/* MOVE SUPPLIER */}
        <select
          className="border rounded px-2 py-1 text-xs"
          onChange={e =>
            moveSupplier(
              supplier,
              group.groupId,
              e.target.value
            )
          }
          defaultValue=""
        >
          <option value="" disabled>
            Move Supplier To
          </option>
          {groups
            .filter(g => g.groupId !== group.groupId)
            .map(g => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupId}
              </option>
            ))}
        </select>
      </div>

      {/* ITEMS */}
      <div className="space-y-2">
        {supplier.items.map(item => (
          <div
            key={item.item_id}
            className="flex justify-between items-center bg-slate-100 rounded p-3"
          >
            <div>
              <div className="font-medium flex gap-2 items-center">
                <Package size={14} />
                {item.item_name}
              </div>
              <div className="text-xs text-slate-500">
                Qty {item.quantity}
                {item.totalWeight ? ` • ${item.totalWeight} kg` : ''}
              </div>
            </div>

            {/* MOVE ITEM */}
            <select
              className="border rounded px-2 py-1"
              defaultValue=""
              onChange={e =>
                moveItem(
                  item,
                  group.groupId,
                  e.target.value
                )
              }
            >
              <option value="" disabled>
                Move Item To
              </option>
              {groups
                .filter(g => g.groupId !== group.groupId)
                .map(g => (
                  <option key={g.groupId} value={g.groupId}>
                    {g.groupId}
                  </option>
                ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
})}


              {/* FOOTER */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-sm text-slate-600">
                  Total Weight: <b>{groupWeight(group.suppliers)} kg</b>
                </div>

                <select
                  className="border rounded p-2"
                  value={group.vehicleId}
                  onChange={e =>
                    setGroups(prev =>
                      prev.map(g =>
                        g.groupId === group.groupId
                          ? { ...g, vehicleId: e.target.value }
                          : g
                      )
                    )
                  }
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.type} value={v.type}>
                      {v.type} (≤ {v.maxWeight} kg)
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  placeholder="Delivery Charge"
                  className="border rounded p-2"
                  value={group.deliveryCharge}
                  onChange={e =>
                    setGroups(prev =>
                      prev.map(g =>
                        g.groupId === group.groupId
                          ? { ...g, deliveryCharge: e.target.value }
                          : g
                      )
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>

        {/* ACTIONS */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={addGroup}
            className="text-blue-600 flex items-center gap-2"
          >
            <Plus size={16} /> Add Delivery Group
          </button>

          <button
            onClick={handleConfirm}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg"
          >
            Start Award Process
          </button>
        </div>
      </div>
    </div>
  );
}
